import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Target } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const money = (v) => `${v < 0 ? "-" : ""}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function budgetStatus(r) {
  if (!(r.budget > 0)) return { level: "none", pct: 0 };
  const pct = Math.min(100, (r.actual / r.budget) * 100);
  if (r.actual > r.budget) return { level: "over", pct };
  return { level: r.actual >= r.budget * 0.8 ? "warn" : "ok", pct };
}

const BAR = { over: "bg-destructive", warn: "bg-gold", ok: "bg-palm", none: "bg-palm" };

function StatusBadge({ level, id }) {
  if (level === "over") return <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-destructive" data-testid={`budget-over-${id}`}><AlertTriangle size={10} /> Over budget</span>;
  if (level === "warn") return <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-crimson">80%+ used</span>;
  return null;
}

function BudgetRow({ r, canEdit, value, onChange }) {
  const { level, pct } = budgetStatus(r);
  const over = level === "over";
  const id = r.category.replace(/\W+/g, "-").toLowerCase();
  return (
    <tr className="border-t border-gold/15" data-testid={`budget-row-${id}`}>
      <td className="p-3 font-medium"><Link to={`/admin/money?tab=reports&preset=expenses_by_category&category=${encodeURIComponent(r.category)}`} className="text-palm underline-offset-4 hover:underline" title="See the expenses behind this line" data-testid={`budget-drill-${id}`}>{r.category}</Link><StatusBadge level={level} id={id} /></td>
      <td className="p-3">{canEdit ? <Input type="number" min="0" step="1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 w-28 text-right font-mono-accent" data-testid={`budget-input-${id}`} /> : <span className="font-mono-accent">{r.budget ? money(r.budget) : "—"}</span>}</td>
      <td className="p-3 text-right font-mono-accent">{money(r.actual)}</td>
      <td className={`p-3 text-right font-mono-accent ${over ? "font-bold text-destructive" : ""}`}>{r.budget ? money(r.remaining) : "—"}</td>
      <td className="p-3 w-48"><div className="h-2 rounded-full bg-gold/20"><div className={`h-2 rounded-full ${BAR[level]}`} style={{ width: `${pct}%` }} /></div>
        <span className="text-[10px] text-muted-foreground">{r.pct !== null ? `${r.pct}%` : "no budget set"}</span></td>
    </tr>
  );
}

function BudgetCard({ r, canEdit, value, onChange }) {
  const { level, pct } = budgetStatus(r);
  const id = r.category.replace(/\W+/g, "-").toLowerCase();
  return (
    <div className="glass rounded-xl p-3 text-sm" data-testid={`budget-card-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{r.category}<StatusBadge level={level} id={`m-${id}`} /></p>
        {canEdit ? <label className="flex items-center gap-2 text-xs text-muted-foreground">Budget <Input type="number" min="0" step="1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-28 text-right font-mono-accent" data-testid={`budget-input-m-${id}`} /></label>
          : <span className="font-mono-accent text-xs">Budget {r.budget ? money(r.budget) : "—"}</span>}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-muted-foreground">Spent</span><p className="font-mono-accent font-semibold">{money(r.actual)}</p></div>
        <div className="text-right"><span className="text-muted-foreground">Remaining</span><p className={`font-mono-accent font-semibold ${level === "over" ? "text-destructive" : ""}`}>{r.budget ? money(r.remaining) : "—"}</p></div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-gold/20"><div className={`h-2 rounded-full ${BAR[level]}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-[10px] text-muted-foreground">{r.pct !== null ? `${r.pct}% used` : "no budget set"}</span>
    </div>
  );
}

export function BudgetVsActual() {
  const year = new Date().getFullYear();
  const { data, reload } = useApi(`/admin/finance/budget?year=${year}`);
  const [draft, setDraft] = useState({});
  useEffect(() => { if (data) setDraft(Object.fromEntries(data.rows.map((r) => [r.category, r.budget || ""]))); }, [data]);
  const save = async () => {
    try { await api.put("/admin/finance/budget", { year, budget: Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])) }); toast.success("Budget saved"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  if (!data) return null;
  const overCount = data.rows.filter((r) => r.budget > 0 && r.actual > r.budget).length;
  return (
    <section data-testid="budget-vs-actual">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="flex items-center gap-2 font-serif-display text-2xl font-bold text-crimson"><Target size={20} className="text-gold" /> Budget vs actual · {year}</h3>
          <p className="text-sm text-muted-foreground">Budget <b className="font-mono-accent text-foreground">{money(data.total_budget)}</b> · Spent <b className="font-mono-accent text-foreground" data-testid="budget-total-actual">{money(data.total_actual)}</b>
            {overCount > 0 && <span className="ml-2 font-semibold text-destructive" data-testid="budget-over-count">{overCount} categor{overCount === 1 ? "y" : "ies"} over budget</span>}</p></div>
        {data.can_edit && <Button onClick={save} className="bg-crimson" data-testid="budget-save">Save budget</Button>}
      </div>
      <div className="space-y-2 lg:hidden" data-testid="budget-cards">
        {data.rows.map((r) => <BudgetCard key={r.category} r={r} canEdit={data.can_edit} value={draft[r.category]} onChange={(v) => setDraft({ ...draft, [r.category]: v })} />)}
      </div>
      <div className="glass hidden overflow-x-auto rounded-2xl lg:block">
        <table className="w-full min-w-[720px] text-sm" data-testid="budget-table">
          <thead className="bg-crimson text-parchment"><tr><th className="p-3 text-left">Category</th><th className="p-3 text-left">Budget</th><th className="p-3 text-right">Actual</th><th className="p-3 text-right">Remaining</th><th className="p-3 text-left">Used</th></tr></thead>
          <tbody>{data.rows.map((r) => <BudgetRow key={r.category} r={r} canEdit={data.can_edit} value={draft[r.category]} onChange={(v) => setDraft({ ...draft, [r.category]: v })} />)}</tbody>
        </table>
      </div>
    </section>
  );
}
